import React, { FC, useCallback, useMemo, useState } from 'react'
import { Button, Heading, Icon, Modal, Text, Checkbox, Input } from '@afterpaytouch/core'
import { useConsumerSizes } from '../../../../../utils/responsive'
import { Section75DisputeModalSteps, SelectedItem, useSection75DisputeModalContext } from '../../Section75DisputeModalContext'
import { isAmountOverSection75Threshold, checkAmount } from '../../../utils'
import { useSection75Eligibility } from '../../hooks'
import { useTranslation } from 'next-i18next'

interface ManualItem {
  id: number
  name: string
  amount: string
}
const I18N_NAMESPACE = ['dispute']

export const ChooseItems: FC = () => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const { textSize } = useConsumerSizes()
  const { setStep, order, selectedItems, setSelectedItems, setCurrentItemIndex } = useSection75DisputeModalContext()
  const items = order?.orderDetail?.items || []

  // State for manual item entry when order details are incomplete
  const [manualItems, setManualItems] = useState<ManualItem[]>([{ id: 1, name: '', amount: '' }])

  // Determine if we should show manual entry form (when no items or incomplete item data)
  const shouldShowManualEntry = useMemo(() => {
    return items.length === 0 || items.some((item) => !item.name || !item.price?.amount)
  }, [items])

  const backHandler = useCallback(() => {
    setStep(Section75DisputeModalSteps.ClaimEligibility)
  }, [setStep])

  const hasSection75Eligibility = useSection75Eligibility(selectedItems)

  // Check if manual items meet Section 75 eligibility and have valid format
  const hasManualItemsEligibility = useMemo(() => {
    return manualItems.some((item) => {
      if (!item.name.trim() || !item.amount.trim()) {
        return false
      }
      const numericAmount = parseFloat(item.amount)
      // Check both valid currency format and Section 75 threshold
      return checkAmount(numericAmount) && isAmountOverSection75Threshold(item.amount)
    })
  }, [manualItems])

  const handleNext = useCallback(() => {
    if (shouldShowManualEntry) {
      // For manual entry, convert manual items to selected items
      const validManualItems = manualItems.filter((item) => item.name.trim() && item.amount.trim())
      if (validManualItems.length > 0) {
        const newSelectedItems: SelectedItem[] = validManualItems.map((item, index) => ({
          key: index,
          name: item.name.trim(),
          amount: {
            amount: item.amount.trim(),
            currency: order?.orderSummary?.orderTotal?.currency || 'GBP',
          },
          reason: '',
          fileKeys: [],
        }))
        setSelectedItems(newSelectedItems)

        // Check if any manual item meets Section 75 eligibility
        if (hasManualItemsEligibility) {
          setCurrentItemIndex(0)
          setStep(Section75DisputeModalSteps.DisputeDetails)
        } else {
          setStep(Section75DisputeModalSteps.NotEligible)
        }
      }
    } else if (selectedItems.length > 0) {
      // Check if selected items meet Section 75 eligibility
      if (hasSection75Eligibility) {
        setCurrentItemIndex(0)
        setStep(Section75DisputeModalSteps.DisputeDetails)
      } else {
        setStep(Section75DisputeModalSteps.NotEligible)
      }
    }
  }, [
    setStep,
    selectedItems.length,
    setCurrentItemIndex,
    shouldShowManualEntry,
    manualItems,
    order,
    setSelectedItems,
    hasManualItemsEligibility,
    hasSection75Eligibility,
  ])

  const handleItemToggle = useCallback(
    (itemKey: number, itemName: string, checked: boolean) => {
      if (checked) {
        const orderItem = items[itemKey]
        const itemPrice = orderItem?.price?.amount ?? '0'
        const itemCurrency = orderItem?.price?.currency ?? 'GBP'

        const newSelectedItem: SelectedItem = {
          key: itemKey,
          name: itemName,
          amount: {
            amount: itemPrice,
            currency: itemCurrency,
          },
          reason: '',
          fileKeys: [],
        }
        setSelectedItems([...selectedItems, newSelectedItem])
      } else {
        setSelectedItems(selectedItems.filter((item) => item.key !== itemKey))
      }
    },
    [selectedItems, setSelectedItems, items]
  )

  // Manual item handlers
  const handleManualItemNameChange = useCallback((itemId: number, name: string) => {
    setManualItems((prev) => prev.map((item) => (item.id === itemId ? { ...item, name } : item)))
  }, [])

  const handleManualItemAmountChange = useCallback((itemId: number, amount: string) => {
    setManualItems((prev) => prev.map((item) => (item.id === itemId ? { ...item, amount } : item)))
  }, [])

  const handleAddAnotherItem = useCallback(() => {
    setManualItems((prev) => {
      const newId = Math.max(...prev.map((item) => item.id), 0) + 1
      return [...prev, { id: newId, name: '', amount: '' }]
    })
  }, [])

  const handleRemoveManualItem = useCallback((itemId: number) => {
    setManualItems((prev) => prev.filter((item) => item.id !== itemId))
  }, [])

  const isNextDisabled = useMemo(() => {
    if (shouldShowManualEntry) {
      return !manualItems.some((item) => {
        if (!item.name.trim() || !item.amount.trim()) {
          return false
        }
        const numericAmount = parseFloat(item.amount)
        return checkAmount(numericAmount)
      })
    }
    return selectedItems.length === 0
  }, [selectedItems, shouldShowManualEntry, manualItems])

  return (
    <>
      <Modal.Header divider={true}>
        <div className='absolute z-10 pt-1'>
          <Button.Link size='Small' onClick={backHandler}>
            <Icon.CaretLeft size='Small' />
          </Button.Link>
        </div>
        <div className='text-center'>
          <Heading size='L'>{t('dispute:section75DisputeModal:chooseItems:heading')}</Heading>
        </div>
      </Modal.Header>
      <Modal.Content>
        {shouldShowManualEntry ? (
          // Manual Entry Form
          <>
            {manualItems.map((item, index) => (
              <div key={item.id} className='mb-6'>
                <div className='mb-4'>
                  <div className='mb-2 flex items-center justify-between'>
                    <Text size={textSize}>{t('dispute:section75DisputeModal:chooseItems:descriptionLabel')}</Text>
                    {manualItems.length > 1 && (
                      <Button.Link onClick={() => handleRemoveManualItem(item.id)} color='Fire'>
                        {t('dispute:section75DisputeModal:chooseItems:removeItem')}
                      </Button.Link>
                    )}
                  </div>
                  <Input.Text
                    label={t('dispute:section75DisputeModal:chooseItems:descriptionLabel')}
                    value={item.name}
                    onChange={(e) => handleManualItemNameChange(item.id, e.target.value)}
                    fullWidth
                  />
                </div>
                <div className='mb-4'>
                  <Text size={textSize} className='mb-2'>
                    {t('dispute:section75DisputeModal:chooseItems:amountLabel')}
                  </Text>
                  <Input.Number
                    label={t('dispute:section75DisputeModal:chooseItems:amountLabel')}
                    value={item.amount}
                    onChange={(e) => handleManualItemAmountChange(item.id, e.target.value)}
                    fullWidth
                  />
                </div>
              </div>
            ))}

            <div className='mb-6'>
              <Button.Link onClick={handleAddAnotherItem}>{t('dispute:section75DisputeModal:chooseItems:addItem')}</Button.Link>
            </div>

            <div className='mt-auto'>
              <Button padding='Fluid' size='Large' onClick={handleNext} disabled={isNextDisabled}>
                {t('dispute:section75DisputeModal:chooseItems:next')}
              </Button>
            </div>
          </>
        ) : (
          // Items List
          <>
            <div>
              <Text size={textSize}>{t('dispute:section75DisputeModal:chooseItems:selectItems')}</Text>
            </div>

            <div className='mt-6 space-y-3'>
              {items.map((item, index) => {
                const itemKey = index
                const itemPrice = item.price?.amount ?? '0'
                const itemSymbol = item.price?.symbol ?? '$'

                return (
                  <div
                    key={`item-${itemKey}`}
                    className='flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:bg-gray-50'
                  >
                    <div className='flex flex-1 items-center'>
                      <Checkbox
                        id={`item-${itemKey}`}
                        name={`item-${itemKey}`}
                        onChange={(event) => handleItemToggle(itemKey, item.name, event.target.checked)}
                        label={item.name}
                        checked={selectedItems.some((selectedItem) => selectedItem.key === itemKey)}
                      />
                    </div>
                    <div className='ml-4 flex-shrink-0'>
                      <Text size={textSize} className='font-medium'>
                        {itemSymbol}
                        {itemPrice}
                      </Text>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className='mt-6'>
              <Button padding='Fluid' size='Large' onClick={handleNext} disabled={isNextDisabled}>
                {t('dispute:section75DisputeModal:chooseItems:next')}
              </Button>
            </div>
          </>
        )}
      </Modal.Content>
    </>
  )
}
